import { useEffect, useState, useRef } from 'react';
import { Api } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';

const fallbackBrands = [
  { id: 'lv', name: 'Louis Vuitton' },
  { id: 'chanel', name: 'Chanel' },
  { id: 'polene', name: 'Polène' },
  { id: 'prada', name: 'Prada' },
  { id: 'valentino', name: 'Valentino' },
  { id: 'longchamp', name: 'Longchamp' },
  { id: 'dg', name: 'Dolce & Gabbana' },
];

const normalizeBrands = (payload) => {
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data
    : [];

  return source.map((brand, index) => ({
    id: brand.brand_id ?? `brand-${index}`,
    name: brand.name || brand.title || `Brand ${index + 1}`,
    logo: brand.logo || brand.image || null,
  }));
};

const BrandsSection = () => {
  const [brands, setBrands] = useState(fallbackBrands);
  const { t } = useLanguage();

  const marqueeRef = useRef(null);
  const trackRef = useRef(null);

  // Fetch brands
  useEffect(() => {
    let mounted = true;

    Api.fetchBrands()
      .then((data) => {
        if (!mounted) return;
        const normalized = normalizeBrands(data);
        if (normalized.length) setBrands(normalized);
      })
      .catch(() => mounted && setBrands(fallbackBrands));

    return () => {
      mounted = false;
    };
  }, []);

  // Build advanced marquee
  useEffect(() => {
    const wrapper = marqueeRef.current;
    const track = trackRef.current;

    if (!wrapper || !track || !brands.length) return;

    // Clear previous items
    track.innerHTML = '';

    // Build 1 round of brand items
    const buildButton = (brand) => {
      const btn = document.createElement('button');
      btn.className = 'brands__item';

      btn.onclick = () => {
        const params = new URLSearchParams();
        params.set('brand', brand.id);
        params.set('brandName', brand.name);
        window.location.assign(`/products?${params.toString()}`);
      };

      const img = document.createElement('img');
      img.src = brand.logo;
      img.alt = brand.name;
      img.loading = 'lazy';

      btn.appendChild(img);
      return btn;
    };

    // Add initial set
    brands.forEach((brand) => track.appendChild(buildButton(brand)));

    // Duplicate until > wrapper.width * 2
    const wrapperWidth = wrapper.offsetWidth;

    while (track.offsetWidth < wrapperWidth * 2) {
      brands.forEach((brand) => track.appendChild(buildButton(brand)));
    }

    // Set speed based on width
    const duration = Math.max(12, track.offsetWidth / 180);
    track.style.animationDuration = `${duration}s`;
  }, [brands]);

  return (
    <section className="brands" aria-label="Featured brands">
      <div className="section-heading">
        <h2>{t('home.top_brands')}</h2>
      </div>

      <div className="brands__marquee" ref={marqueeRef}>
        <div className="brands__track" ref={trackRef}></div>
      </div>
    </section>
  );
};

export default BrandsSection;
